import Dialog from "primevue/dialog";
import Drawer from "primevue/drawer";

/**
 * Where focus lands when a Dialog or Drawer opens.
 *
 * PrimeVue focuses the first `[autofocus]` element inside, and when there is none it falls
 * back to the close (×) button — Dialog even forces the focus ring on it
 * (`focus(closeButton, { focusVisible: true })`, `primevue/dialog`), so every modal opened
 * with the mouse came up with a lit ×. PrimeVue has no option to change that fallback.
 *
 * Here the fallback is the panel itself: focus still moves into the modal, so Tab starts
 * inside it and screen readers announce it, but nothing is highlighted. An explicit
 * `autofocus` on a field keeps working as before.
 *
 * `this` is the PrimeVue component instance — `container` is the panel element both
 * components keep a ref to.
 */
export function focusPanelOnOpen(this: { container: HTMLElement | null }) {
  const panel = this.container;
  if (!panel) return;

  const requested = panel.querySelector<HTMLElement>("[autofocus]");
  if (requested) {
    requested.focus();
    return;
  }

  if (!panel.hasAttribute("tabindex")) panel.setAttribute("tabindex", "-1");
  panel.focus({ preventScroll: true });
}

type WithMethods = { methods: Record<string, unknown> };

/**
 * Swaps the fallback in once, before the app mounts. Every import of `primevue/dialog` —
 * the global registration and the local ones alike — is the same component object, so
 * this reaches all of them, including modals added later.
 */
export const installOverlayFocus = () => {
  for (const component of [Dialog, Drawer] as unknown as WithMethods[]) {
    component.methods.focus = focusPanelOnOpen;
  }
};
